import * as React from 'react';
import classnames from 'classnames';
import { t } from 'i18next';
import { Button, IconButton } from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Close as CloseIcon } from '@mui/icons-material';
import imageCompression from 'browser-image-compression';
import ApiManager from '../../libs/ApiManager';
import Progress from '../utils/Progress';
import { PreviewTrigger } from '../utils/PreviewModal';
import ErrorMessageContext from '../context/ErrorMessageContext';
import SubmitFormContext from '../context/SubmitFormContext';

import '../../styles/FileUpload.scss';

export const FILE_UPLOAD_ERRORS = {
  TOO_MANY_FILES: new Error('TOO_MANY_FILES'),
  TOO_LARGE_FILES: new Error('TOO_LARGE_FILES'),
}

function readAsDataURL (file) {
  const options = {
    maxSizeMB: 0.7,
    maxWidthOrHeight: 1080,
    useWebWorker: true
  };

  if (/video/.test(file.type)) {
    return readFile(file);
  }

  return imageCompression(file, options).then(compressFile => {
    return readFile(compressFile);
  });

  function readFile (file) {
    return new Promise(res => {
      var r = new FileReader();
      r.onload = function() {
        res(r.result);
      };
      r.readAsArrayBuffer(file);
    });
  }
}

async function getVideoCover (source) {
  return new Promise((resolve, reject) => {
    // load the file to a video player
    const videoPlayer = document.createElement('video');
    let src = source;
    if (source instanceof File) {
      src = URL.createObjectURL(source);
    }
    videoPlayer.setAttribute('src', src);
    videoPlayer.setAttribute('crossOrigin', 'Anonymous');
    videoPlayer.load();
    videoPlayer.addEventListener('error', ex => {
      reject('error when loading video file', ex);
    });
    // load metadata of the video to get video duration and dimensions
    videoPlayer.addEventListener('loadedmetadata', () => {
      // seek to user defined timestamp (in seconds) if possible
      // delay seeking or else 'seeked' event won't fire on Safari
      setTimeout(() => {
        videoPlayer.currentTime = 1.0;
      }, 200);
      // extract video thumbnail once seeking is complete
      videoPlayer.addEventListener('seeked', () => {
        // define a canvas to have the same dimension as the video
        const canvas = document.createElement('canvas');
        canvas.width = videoPlayer.videoWidth;
        canvas.height = videoPlayer.videoHeight;
        // draw the video frame to canvas
        const ctx = canvas.getContext('2d');
        ctx.drawImage(videoPlayer, 0, 0, canvas.width, canvas.height);
        // return the canvas image as a blob
        ctx.canvas.toBlob(
          blob => {
            resolve(URL.createObjectURL(blob));
          },
          'image/jpeg',
          0.75 /* quality */
        );
      });
    });
  });
}

const CANCEL_UPLOAD_ERROR = 'CANCEL_UPLOAD_ERROR';

function FilePreview ({ id, data, file, token, onChange = () => {}, onDelete = () => {}, onError = () => {} }) {
  const [progress, setProgress] = React.useState(0);
  const [preview, setPreview] = React.useState(null);
  const { pending, resolve } = React.useContext(SubmitFormContext);
  const mounted = React.useRef(false);

  React.useEffect(() => {
      mounted.current = true;
      return () => { mounted.current = false; };
  }, []);

  React.useEffect(() => {
    let pendingKey = `${id}_${Date.now()}`;
    let cancelSource;
    async function uploadFile () {
      try {
        if (file instanceof File) {
          // When file is exists, then show preview.
          const type = /video/.test(file.type) ? 'video' : 'image';
          if (type === 'video') {
            const snapshot = await getVideoCover(file);
            setPreview({ url: URL.createObjectURL(file), type, poster: snapshot });
          } else {
            setPreview({ url: URL.createObjectURL(file), type });
          }
          // When data is not uploaded then upload the file.
          if (!data) {
            pending(pendingKey);
            if (type === 'image') {
              setProgress(Math.random() * 50);
            } else {
              setProgress(0);
            }
            const data = await readAsDataURL(file);
            const { source, uploadImage } = ApiManager.uploadImageWithCancel(data, file.type, token, e => {
              const percentage = Math.round((e.loaded * 100) / e.total) || 0;
              if (mounted.current && percentage < 100) {
                setProgress(percentage);
              }
            });
            cancelSource = source;
            const { presignedGetURL, fileName } = await uploadImage;
            cancelSource = null;
            if (mounted.current) {
              setProgress(100);
              onChange({
                id,
                data: {
                  key: fileName,
                  url: presignedGetURL,
                  type,
                  size: file.size,
                },
              });
              setTimeout(() => {
                mounted.current && setProgress(101);
                if (pendingKey) {
                  resolve(pendingKey);
                  pendingKey = null;
                }
              }, 500);
            }
          }
        } else if (data) {
          // When data is uploaded, but file is not exists.
          if (data.type === 'video') {
            setPreview({
              ...data,
              poster: await getVideoCover(data.url),
            });
          } else {
            setPreview(data);
          }
          setProgress(101);
        }
      } catch (err) {
        if (err.message !== CANCEL_UPLOAD_ERROR) {
          onError(err, id);
        }
      }
    }
    uploadFile();

    return () => {
      if (pendingKey) {
        resolve(pendingKey);
        pendingKey = null;
      }
      if (cancelSource) {
        cancelSource.cancel(CANCEL_UPLOAD_ERROR);
      }
    }
  }, [file, data]);

  const onDeleteBtnClick = (e) => {
    e.stopPropagation();
    onDelete(id)
  }

  const url = preview?.poster || preview?.url;

  return <PreviewTrigger>
    {
      (handler, open) => <div className={classnames('file-item', { 'uploading': progress <= 100 })} onClick={() => open(preview?.url, preview?.type)}>
        { url && <img className='poster' src={url} onError={(e) => onError(e, id)} alt='poster' /> }
        <IconButton className='file-item-delete-btn' onClick={onDeleteBtnClick} sx={{ zIndex: 2 }}>
            <CloseIcon sx={{ width: '16px', height: '16px' }} />
        </IconButton>
        <Progress size={24} variant='determinate' value={progress} style={{ background: 'none' }} show={progress <= 100} />
      </div>
    }    
  </PreviewTrigger>
}

function FileUploadButton ({
  id,
  accept,
  placeholder,
  fileList,
  maxNumber,
  maxSize,
  multiple,
  token,
  onChange = (files) => {},
  onError = (error) => {},
  ...props
}) {
  const uploadFile = (e) => {
    try {
      const files = Array.from(e.target.files);
      const filesLength = files.length;
      if (filesLength === 0) return;
      const tooManyFiles = fileList.length + filesLength > maxNumber;
      if (tooManyFiles) {
        throw FILE_UPLOAD_ERRORS.TOO_MANY_FILES;
      }
      const maxSizeMB = maxSize * 1048576;
      const tooLargeFiles = [...fileList.map(file => (file.data || file.file)), ...files].reduce((sum, file) => sum += (file?.size || 0), 0) > maxSizeMB;
      if (tooLargeFiles) {
        throw FILE_UPLOAD_ERRORS.TOO_LARGE_FILES;
      }
      onChange(files);
    } catch (error) {
      onError(error);
    } finally {
      e.target.value = '';
    }
  }

  return <Button variant='contained' component='label' {...props}>
    <FileUploadIcon style={{ marginRight: 5, fontSize: 16 }} />
    { placeholder }
    <input type='file' multiple={multiple} accept={accept} onChange={uploadFile} hidden />
  </Button>
}

export default function FileUpload ({
  placeholder,
  multiple,
  image,
  video,
  accept,
  token,
  platform,
  maxNumber = 3,
  maxSize = 50,
  value,
  onChange,
  ...props
}) {
  const { onError } = React.useContext(ErrorMessageContext);
  const [buttons, setButtons] = React.useState([]);

  React.useEffect(() => {
    const now = Date.now();
    let newButtons;
    if (platform === 'ios') {
      const basicAccept = [
        image ? 'image/*' : null,
        video ? 'video/*' : null,
      ].filter(Boolean).join(',');
      newButtons = [basicAccept !== '' ? { accept: basicAccept, placeholder } : null, ...(accept ? accept.split(',').map(acceptData => ({
        accept: acceptData,
        placeholder,
      })) : [])].filter(Boolean);
    } else {
      newButtons = [
        image ? {
          accept: 'image/*',
          placeholder: t('file_upload.control.pictures'),
        } : null,
        video ? {
          accept: 'video/*',
          placeholder: t('file_upload.control.videos'),
        } : null,
        ...(accept ? accept.split(',').map(acceptData => ({
          accept: acceptData,
          placeholder,
        })) : [])
      ].filter(Boolean);
    }
    setButtons(newButtons.map((data, index) => ({
      ...data,
      id: now + index,
      isUploading: false,
    })));
  }, [image, video, accept, platform, placeholder]);

  const onButtonChange = (files) => {
    const now = Date.now();
    onChange([...value, ...files.map((file, index) => Object.assign({}, { id: now + index, file }))]);
  }

  const onPreviewChange = (file) => {
    const list = [...value];
    const curr = list.find(f => f.id === file.id);
    if (curr) {
      delete curr.file;
      Object.assign(curr, file);
    } else {
      list.push(file);
    }
    onChange(list);
  }

  const onPreviewDelete = (id) => {
    onChange(value.filter(i => i.id !== id));
  }

  const onUploadError = (error) => {
    switch (error) {
      case FILE_UPLOAD_ERRORS.TOO_LARGE_FILES:
        return onError(new Error(t('file_upload.failed.limit')));
      case FILE_UPLOAD_ERRORS.TOO_MANY_FILES:
        return onError(new Error(t('file_upload.failed.limit')));
      default:
        return onError(error);
    }
  }

  const onPreviewError = (error, id) => {
    onPreviewDelete(id);
  }

  return <div className='file-upload'>
    <div className='file-upload-button-container'>
    {
      buttons.map(button => <FileUploadButton
        {...props}
        key={`file-upload-btn-${button.id}`}
        id={button.id}
        accept={button.accept}
        placeholder={button.placeholder}
        fileList={value}
        maxNumber={maxNumber}
        maxSize={maxSize}
        token={token}
        multiple={multiple}
        onChange={onButtonChange}
        onError={onUploadError}
      />)
    }
    </div>
    <div className="file-upload-coda">
      {t("file_upload.control.coda", {
        number: value.length,
        max_number: maxNumber,
        max_size: maxSize,
      })}
    </div>
    {value.length > 0 && <div className='file-list'>
      { value.map((file, index) => <FilePreview key={index} {...file} token={token} onChange={onPreviewChange} onDelete={onPreviewDelete} onError={onPreviewError} />) }
    </div>}
  </div>
}